/**
 * `<FailedExtractionTriage>` — the failed-extraction triage table (frontend.md §2.1, §1.5).
 *
 * "No silent failures" (conventions.md §3 / §7): a retry-exhausted extraction job leaves a
 * `failed` queue row that surfaces HERE — never a silent drop. The table shows the note,
 * the stage, attempts, the last error + its category, and a per-row "Retry" action wired
 * to `extraction/retry` (`useRetryExtractionMutation`, which invalidates the health
 * queries on success).
 *
 * U7 (2026-07-05):
 *  - rows are keyed by note TITLE — the failed-extraction payload only carries `noteId`,
 *    so titles are joined client-side from the `note_titles` tool (the same lightweight
 *    projection the tree/autocomplete use). Unresolvable ids fall back to an 8-char mono
 *    id that copies the full UUID on click.
 *  - `last_error` is a serialized `AletheiaError.to_context()` JSON blob
 *    (`{error_type, category, retryable, …}`) — it's parsed (defensively; the column can
 *    truncate) into an error-type chip + a "terminal" hint, with the raw JSON behind a
 *    per-row disclosure.
 *  - "Retry all failed" beside the header loops the existing per-row retry mutation.
 *
 * Virtualized (`<VirtualizedTable>`) — at 100K notes a backlog of failures can be long
 * (D-009 / frontend.md §1.5). Cursor-style paging via the page index passed to the hook.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { noteTitles } from '@/api';
import type { FailedExtractionItem } from '@/types';
import { useFailedExtractionsQuery, useRetryExtractionMutation } from '@/hooks';
import { LoadingState, EmptyState, VirtualizedTable } from '@/components';
import type { TableColumn } from '@/components';
import { HealthCard } from './HealthCard';

interface FailedExtractionTriageProps {
  active: boolean;
}

/** What we can salvage from the serialized `AletheiaError.to_context()` blob. */
interface ParsedLastError {
  errorType: string | null;
  category: string | null;
  retryable: boolean | null;
  raw: string;
}

/**
 * Parse the `last_error` column. It's `json.dumps(error.to_context())` on the backend —
 * `{"error_type": …, "category": …, "retryable": …, …}` — but the stored/transported string
 * can be truncated, so fall back to regex salvage before giving up.
 */
function parseLastError(raw: string | null): ParsedLastError | null {
  if (raw === null || raw.trim() === '') return null;
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    return {
      errorType: typeof obj['error_type'] === 'string' ? obj['error_type'] : null,
      category: typeof obj['category'] === 'string' ? obj['category'] : null,
      retryable: typeof obj['retryable'] === 'boolean' ? obj['retryable'] : null,
      raw,
    };
  } catch {
    const type = /"error_type"\s*:\s*"([^"]+)"/.exec(raw);
    const category = /"category"\s*:\s*"([^"]+)"/.exec(raw);
    const retryable = /"retryable"\s*:\s*(true|false)/.exec(raw);
    return {
      errorType: type?.[1] ?? null,
      category: category?.[1] ?? null,
      retryable: retryable !== null ? retryable[1] === 'true' : null,
      raw,
    };
  }
}

/** Pretty-print the raw blob for the disclosure when it parses; show it verbatim when not. */
function prettyRaw(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function FailedExtractionTriage({ active }: FailedExtractionTriageProps): JSX.Element {
  const [page] = useState(0);
  const failedQuery = useFailedExtractionsQuery(page, { enabled: active });
  const retryMutation = useRetryExtractionMutation();
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());
  const [retryingAll, setRetryingAll] = useState(false);

  const items = useMemo<FailedExtractionItem[]>(
    () => failedQuery.data?.items ?? [],
    [failedQuery.data],
  );

  // Title join — the failed-extraction payload only carries noteId. `note_titles` is the
  // existing lightweight {noteId, title} projection (autocomplete/tree); fetch once and
  // join client-side. Cached + persisted under the 'note-titles' key family.
  const titlesQuery = useQuery({
    queryKey: ['note-titles', 'failed-triage'],
    queryFn: () => noteTitles({ limit: 1000 }),
    enabled: active && items.length > 0,
  });
  const titleById = useMemo(
    () => new Map((titlesQuery.data?.data.items ?? []).map((i) => [i.noteId, i.title])),
    [titlesQuery.data],
  );

  const toggleRaw = (jobId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  // "Retry all failed" — loops the existing per-row mutation (same invalidation +
  // notification semantics); stops at the first failure so a dead endpoint doesn't
  // fan out N error toasts.
  const retryAll = async (): Promise<void> => {
    setRetryingAll(true);
    try {
      for (const item of items) {
        await retryMutation.mutateAsync({ jobId: item.jobId });
      }
    } catch {
      // Already surfaced by the mutation's onError handler.
    } finally {
      setRetryingAll(false);
    }
  };

  const busy = retryingAll || retryMutation.isPending;

  const columns: TableColumn<FailedExtractionItem>[] = [
    {
      id: 'note',
      header: 'Note',
      width: '1.5fr',
      cell: (r) => {
        const title = titleById.get(r.noteId);
        if (title !== undefined) {
          return (
            <span className="fx-note" title={r.noteId}>
              {title}
            </span>
          );
        }
        return (
          <button
            type="button"
            className="fx-note-id"
            title={`${r.noteId} — click to copy the full id`}
            onClick={() => void navigator.clipboard.writeText(r.noteId)}
          >
            {r.noteId.slice(0, 8)}
          </button>
        );
      },
    },
    { id: 'stage', header: 'Stage', cell: (r) => r.stage, width: '80px' },
    { id: 'attempts', header: 'Attempts', cell: (r) => r.attempts, width: '90px' },
    {
      id: 'category',
      header: 'Category',
      cell: (r) => r.lastErrorCategory ?? parseLastError(r.lastError)?.category ?? '—',
      width: '110px',
    },
    {
      id: 'error',
      header: 'Last error',
      width: '2fr',
      cell: (r) => {
        const parsed = parseLastError(r.lastError);
        if (parsed === null) return '—';
        const open = expandedIds.has(r.jobId);
        return (
          <div className="fx-error">
            <div className="fx-error__line">
              <span className="chip">{parsed.errorType ?? 'unclassified'}</span>
              {parsed.retryable === false && <span className="fx-error__hint">terminal</span>}
              <button
                type="button"
                className="fx-error__toggle"
                aria-expanded={open}
                onClick={() => toggleRaw(r.jobId)}
              >
                {open ? 'raw ▾' : 'raw ▸'}
              </button>
            </div>
            {open && <pre className="fx-error__raw">{prettyRaw(parsed.raw)}</pre>}
          </div>
        );
      },
    },
    {
      id: 'action',
      header: '',
      width: '90px',
      cell: (r) => (
        <button
          type="button"
          className="btn btn--sm"
          disabled={busy}
          onClick={() => retryMutation.mutate({ jobId: r.jobId })}
        >
          Retry
        </button>
      ),
    },
  ];

  return (
    <HealthCard
      title="Failed extractions"
      anchorId="failed-extractions"
      actions={
        items.length > 0 ? (
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy}
            onClick={() => void retryAll()}
          >
            {retryingAll ? 'Retrying…' : 'Retry all failed'}
          </button>
        ) : undefined
      }
    >
      {failedQuery.isLoading && <LoadingState label="Loading failed jobs…" />}
      {failedQuery.isError && (
        <EmptyState title="Could not load failed extractions" detail="See the notification." />
      )}
      {failedQuery.data !== undefined && items.length === 0 && (
        <EmptyState title="No failed extractions" detail="Every extraction job has succeeded." />
      )}
      {failedQuery.data !== undefined && items.length > 0 && (
        <div style={{ height: 320 }}>
          <VirtualizedTable
            columns={columns}
            rows={items}
            getKey={(r) => r.jobId}
            ariaLabel="Failed extraction jobs"
          />
        </div>
      )}
    </HealthCard>
  );
}
